import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useTenant } from "../lib/tenant";
import { CalendarDays, Receipt, FileText, Network, Inbox, Sparkles, TrendingUp, Clock, Bell, AlertTriangle, Info, CheckCircle2, ArrowRight, RefreshCw, X } from "lucide-react";

const QuickAction = ({ icon: Icon, label, onClick, testid, accent }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="sw-card group text-left hover:border-[#475569] hover:-translate-y-0.5"
  >
    <div className={`w-12 h-12 rounded-[14px] flex items-center justify-center mb-4 transition-colors ${accent === "gold" ? "bg-[#e2e8f0] text-[#475569]" : "bg-[#f6f6f6] text-[#323232]"} group-hover:bg-[#475569] group-hover:text-white`}>
      <Icon size={22} />
    </div>
    <div className="font-medium text-[15px] text-[#323232]">{label}</div>
  </button>
);

const StatCard = ({ label, value, suffix, icon: Icon, hint }) => (
  <div className="sw-card sw-fade-up">
    <div className="flex items-center justify-between mb-3">
      <span className="sw-label !mb-0">{label}</span>
      <Icon size={18} className="text-[#475569]" />
    </div>
    <div className="flex items-baseline gap-2">
      <span className="text-[44px] font-semibold text-[#323232] leading-none tracking-tight">{value}</span>
      {suffix && <span className="text-[14px] text-[#747474]">{suffix}</span>}
    </div>
    {hint && <div className="text-[12px] text-[#747474] mt-2">{hint}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { tenant } = useTenant();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], count: 0, critical_count: 0 });

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/alerts/my").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  const dismissOne = async (alertId) => {
    // Optimistic update
    setAlerts((prev) => ({
      ...prev,
      alerts: prev.alerts.filter((a) => a.id !== alertId),
      count: Math.max(0, prev.count - 1),
    }));
    try {
      await api.post("/alerts/dismiss", { alert_id: alertId });
    } catch {
      // Reload on failure
      api.get("/alerts/my").then((r) => setAlerts(r.data)).catch(() => {});
    }
  };

  const dismissAll = async () => {
    setAlerts({ alerts: [], count: 0, critical_count: 0 });
    try {
      await api.post("/alerts/dismiss-all");
    } catch {
      api.get("/alerts/my").then((r) => setAlerts(r.data)).catch(() => {});
    }
  };

  if (!data) {
    return <div className="text-[#747474]">{t("common.loading")}</div>;
  }

  const firstName = user?.name?.split(" ")[0] || "";
  const draftAlerts = alerts.alerts.filter((a) => a.id.includes("-draft-"));
  const reimbDrafts = draftAlerts.filter((a) => a.id.startsWith("reimb-draft-"));
  const travelDrafts = draftAlerts.filter((a) => a.id.startsWith("travel-draft-"));

  return (
    <div className="flex flex-col gap-8">
      {/* Sprint 28.3 — Prominent drafts banner (action-required) */}
      {draftAlerts.length > 0 && (
        <div
          data-testid="dashboard-drafts-banner"
          className="relative rounded-[20px] overflow-hidden border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 flex items-center justify-between gap-4 shadow-sm sw-fade-up"
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center shrink-0">
              <RefreshCw size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] text-amber-900">
                {draftAlerts.length === 1
                  ? t("drafts.banner_title_one")
                  : t("drafts.banner_title_many", { n: draftAlerts.length })}
              </div>
              <div className="text-[12.5px] text-amber-800 mt-0.5">
                {reimbDrafts.length > 0 && (
                  <span>{reimbDrafts.length === 1 ? t("drafts.count_reimb_one") : t("drafts.count_reimb_many", { n: reimbDrafts.length })}</span>
                )}
                {reimbDrafts.length > 0 && travelDrafts.length > 0 && <span> · </span>}
                {travelDrafts.length > 0 && (
                  <span>{travelDrafts.length === 1 ? t("drafts.count_travel_one") : t("drafts.count_travel_many", { n: travelDrafts.length })}</span>
                )}
                {" — "}{t("drafts.banner_help")}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {reimbDrafts.length > 0 && (
              <button
                data-testid="dashboard-drafts-reimb-btn"
                onClick={() => nav("/app/reimbursements")}
                className="px-4 py-2 rounded-[10px] bg-white text-amber-900 hover:bg-amber-50 border border-amber-300 text-[13px] font-medium flex items-center gap-1.5 transition"
              >
                {t("drafts.cta_reimb")} <ArrowRight size={13} />
              </button>
            )}
            {travelDrafts.length > 0 && (
              <button
                data-testid="dashboard-drafts-travel-btn"
                onClick={() => nav("/app/travel")}
                className="px-4 py-2 rounded-[10px] bg-amber-900 text-white hover:bg-amber-950 text-[13px] font-medium flex items-center gap-1.5 transition"
              >
                {t("drafts.cta_travel")} <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Hero greeting */}
      <div
        className="relative rounded-[32px] overflow-hidden p-10 lg:p-12 text-white sw-fade-up"
        style={{
          background:
            "linear-gradient(135deg, #1e293b 0%, #475569 50%, #334155 100%)",
        }}
      >
        <div className="absolute inset-0 sw-grain opacity-60" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur rounded-full px-3 py-1 text-[11px] uppercase tracking-wider mb-5">
            <Sparkles size={12} />
            <span>{t("dash.welcome_chip")}</span>
          </div>
          <h1 className="sw-heading text-white text-[32px] md:text-[44px] leading-tight">
            {t("dash.greeting")}, {firstName}
          </h1>
          <p className="text-white/80 mt-2 text-[15px] max-w-xl">
            {t("dash.welcome")}. {t("app.tagline")}
          </p>
          <div className="mt-6 flex items-center gap-6 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] text-white/50">{t("dash.our_values")}</span>
            <div className="flex items-center gap-x-5 gap-y-2 flex-wrap text-[12px] font-medium">
              <span className="text-white/85">{t("values.quality")}</span>
              <span className="text-white/30">·</span>
              <span className="text-white/85">{t("values.care")}</span>
              <span className="text-white/30">·</span>
              <span className="text-white/85">{t("values.commitment")}</span>
            </div>
          </div>
          <div className="absolute top-6 right-6 hidden md:flex items-center gap-2 text-white/70 text-[10px] uppercase tracking-[0.16em]">
            {tenant?.anniversary_badge && (
              <span className="bg-[#dac28a]/30 text-[#dac28a] px-2 py-1 rounded-full font-semibold">{tenant.anniversary_badge}</span>
            )}
          </div>
        </div>
      </div>

      {/* Smart Alerts */}
      {alerts.count > 0 && (
        <div data-testid="dashboard-alerts" className="sw-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-[#475569]" />
              <h2 className="sw-heading text-[18px]">{t("dash.smart_alerts")}</h2>
              <span className={`sw-badge ${alerts.critical_count > 0 ? "sw-badge-rejected" : "sw-badge-gold"}`}>{alerts.count}</span>
            </div>
            {alerts.alerts.length > 0 && (
              <button
                data-testid="dashboard-alerts-clear-all"
                onClick={dismissAll}
                className="text-[11px] text-[#475569] hover:text-[#1e293b] underline"
              >
                {t("dash.dismiss_all")}
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {alerts.alerts.slice(0, 5).map((a) => (
              <div
                key={a.id}
                data-testid={`dash-alert-${a.type}`}
                className={`text-left p-3 rounded-[12px] flex items-center gap-3 transition-all ${a.severity === "critical" ? "bg-[#ffefef] border-l-2 border-[#881a27]" : a.severity === "warning" ? "bg-[#fef3c7] border-l-2 border-[#92400e]" : "bg-[#f6f6f6] border-l-2 border-[#475569]"}`}
              >
                <button
                  type="button"
                  onClick={() => nav(a.link)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80"
                >
                  {a.severity === "critical" ? <AlertTriangle size={14} className="text-[#881a27]" /> : a.severity === "warning" ? <AlertTriangle size={14} className="text-[#92400e]" /> : <Info size={14} className="text-[#475569]" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[13px] text-[#323232]">{a.title_key ? t(a.title_key) : a.title}</div>
                    <div className="text-[11px] text-[#747474] mt-0.5">{a.description}</div>
                  </div>
                  <ArrowRight size={14} className="text-[#747474]" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); dismissOne(a.id); }}
                  data-testid={`dash-alert-dismiss-${a.id}`}
                  title={t("dash.dismiss_alert")}
                  className="text-[#94a3b8] hover:text-[#1e293b] p-1 rounded hover:bg-white/50 transition shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {alerts.count > 5 && <div className="text-[11px] text-center text-[#747474] pt-1">{t("dash.more_alerts", { n: alerts.count - 5 })}</div>}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label={t("dash.pto_balance")} value={data.pto_balance} suffix={t("dash.days")} icon={Clock} hint={t("dash.hint_pto")} />
        <StatCard label={t("dash.vac_balance")} value={data.vacation_balance} suffix={t("dash.days")} icon={CalendarDays} hint={t("dash.hint_vac")} />
        <StatCard label={t("dash.my_requests")} value={data.my_pto_pending + data.my_reimb_pending} icon={TrendingUp} hint={t("dash.hint_pending")} />
        <StatCard label={t("dash.pending_approvals")} value={data.pending_approvals_pto + data.pending_approvals_reimb} icon={Inbox} hint={t("dash.hint_inbox")} />
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="sw-heading text-[20px] mb-5">{t("dash.quick_actions")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <QuickAction icon={CalendarDays} label={t("dash.request_pto")} accent="gold" testid="action-pto" onClick={() => nav("/app/pto")} />
          <QuickAction icon={Receipt} label={t("dash.submit_reimb")} accent="gold" testid="action-reimb" onClick={() => nav("/app/reimbursements")} />
          <QuickAction icon={FileText} label={t("dash.view_docs")} testid="action-docs" onClick={() => nav("/app/documents")} />
          <QuickAction icon={Network} label={t("dash.view_org")} testid="action-org" onClick={() => nav("/app/org-chart")} />
        </div>
      </div>
    </div>
  );
}
